/**
 * DRIVER & DELIVERY MANAGEMENT — Liefersoft-style unified page
 * Split layout: Order list (left) + Live map (right)
 * All data from Firestore in real-time — no mock data
 * Fully translated: English, German, Dutch
 */
import React, { useState, useEffect, useRef, useMemo, Component } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Avatar, List, ListItem, ListItemAvatar, ListItemText,
  CircularProgress, Divider,
} from '@mui/material';
import {
  LocalShipping, Person, LocationOn, Phone, CheckCircle, Cancel, Print, Visibility,
  CalendarToday, Refresh, Close, AssignmentInd, Navigation,
  DeliveryDining, FiberManualRecord,
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import { BRANCH_LOCATION, calculateDistance } from '../../config/branchLocation';
import { useLanguage } from '../../contexts/LanguageContext';
import { LoadScript, GoogleMap, Marker, InfoWindow, Polyline, OverlayView } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../config/googleMapsConfig';

// ─── Types ───────────────────────────────────────────────────────
interface DeliveryOrder {
  id: string;
  order_number: number;
  customer_name: string;
  delivery_address: string;
  phone?: string;
  total: number;
  item_count: number;
  status: string;
  rider_email?: string;
  rider_name?: string;
  created_at: Date;
  items?: any[];
  payment_method?: string;
  notes?: string;
  delivery_latitude?: number;
  delivery_longitude?: number;
  platform?: string;
  trip_number?: number | string;
  // Customer identity fields
  customer_email?: string;
  customer_uid?: string;
  is_guest?: boolean;
  source?: string; // 'qr_order', 'webshop', etc.
  created_by?: string;
  // Fraud prevention #1: Delivery PIN
  delivery_pin?: string;
  delivery_pin_verified?: boolean;
}

interface DriverInfo {
  id: string;
  name: string;
  map_code: string;   // Short 1–4 char label shown on the map marker (e.g. "F01")
  email: string;
  phone: string;
  vehicle_type: string;
  license_plate: string;
  status: string;
  is_available: boolean;
  is_active: boolean;
  latitude?: number;
  longitude?: number;
  deliveries_today: number;
  total_deliveries: number;
  rating: number;
}

// ─── Map Error Boundary ───────────────────────────────────────────
// Catches invariant errors thrown by @react-google-maps/api when
// window.google is absent after script load (expired/restricted key).
class MapErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error) {
    console.error('[MapErrorBoundary] Google Maps failed to load:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{
          width: '100%', height: '100%', minHeight: 300,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', bgcolor: '#1a1d2e', color: '#9ca3af',
          borderRadius: 1, p: 3, gap: 1,
        }}>
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
            🗺️ Karte nicht verfügbar — Google Maps konnte nicht geladen werden
          </Typography>
          <Typography variant="caption" sx={{ color: '#4a4d62', fontSize: '0.7rem' }}>
            {this.state.error?.message}
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────
const statusColor = (s: string) => {
  if (s === 'pending' || s === 'confirmed') return '#FF9800';
  if (s === 'assigned') return '#2196F3';
  if (s === 'picked_up' || s === 'in_transit' || s === 'on_delivery') return '#00BCD4';
  if (s === 'delivered' || s === 'completed') return '#4CAF50';
  if (s === 'cancelled') return '#F44336';
  return '#9ca3af';
};
const statusKey = (s: string): string => {
  if (s === 'pending') return 'drivers.status_new';
  if (s === 'confirmed') return 'drivers.status_confirmed';
  if (s === 'assigned') return 'drivers.status_assigned';
  if (s === 'picked_up') return 'drivers.status_picked_up';
  if (s === 'in_transit' || s === 'on_delivery') return 'drivers.status_on_the_way';
  if (s === 'delivered' || s === 'completed') return 'drivers.status_completed';
  if (s === 'cancelled') return 'drivers.status_cancelled';
  return s;
};
const fmtTime = (d: Date) => `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;

/// Assign tour numbers — last 2 digits of order number (client request: e.g. order 11205 → Tour 05)
const assignTripNumbers = (orders: DeliveryOrder[]): DeliveryOrder[] => {
  for (const order of orders) {
    const numStr = String(order.order_number || '');
    order.trip_number = numStr.length >= 2 ? numStr.slice(-2) : numStr.padStart(2, '0');
  }
  return orders;
};
const today = new Date();
const dateStr = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;

// ─── Google Maps icon builders (SVG data URIs) ───────────────────
const orderIconSvg = (orderNum: number | string, status: string): string => {
  const color = statusColor(status);
  const text = String(orderNum);
  const svg = `<svg width="32" height="32" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <circle cx="16" cy="16" r="13" fill="${color}" stroke="#fff" stroke-width="2.5"/>
    <text x="16" y="20" text-anchor="middle" font-family="Arial,sans-serif" font-size="11" font-weight="700" fill="#fff">${text}</text>
  </svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

// ─── OSRM Route Fetcher ─────────────────────────────────────────
const fetchOSRMRoute = async (
  from: [number, number], to: [number, number]
): Promise<{ coords: Array<{ lat: number; lng: number }>; distance: number; duration: number } | null> => {
  try {
    // OSRM uses lng,lat order (not lat,lng)
    const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const data = await resp.json();
    if (!data.routes || data.routes.length === 0) return null;
    const route = data.routes[0];
    // GeoJSON coordinates are [lng, lat] — flip to {lat, lng} for Google Maps
    const coords = route.geometry.coordinates.map(
      (c: [number, number]) => ({ lat: c[1], lng: c[0] })
    );
    return { coords, distance: route.distance / 1000, duration: route.duration / 60 };
  } catch (e) {
    console.warn('[OSRM] Route fetch failed:', e);
    return null;
  }
};

// ─── Google Maps dark style ─────────────────────────────────────
// Client requirement (2026-04-27): only the Saheeh branch logo and driver
// pins should be visible. Hide ALL Google POIs (restaurants, hospitals,
// schools, transit stops, ATMs, etc.) so the map stays clean.
const GMAPS_DARK_STYLE: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1a1d2e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1d2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#cfd2d9' }] },
  // Hide every POI (icon AND label) — Owner wants only the Saheeh branch.
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  // Keep parks tinted (green geometry) but no labels — gives the map shape without clutter.
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#25392a' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  // Hide transit lines + stops (the H bus/train stops were cluttering the map)
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d3048' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1f2238' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca3af' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3a3d52' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#2d3048' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1424' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a5568' }] },
];

// ─── Google Maps light style ────────────────────────────────────
// Light-mode equivalent — same POI cleanup as dark, but with the
// default light geometry preserved so the map stays readable.
const GMAPS_LIGHT_STYLE: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];

const MAP_LOGO = {
  dark: '/saheeh_logo_w.svg',
  light: '/saheeh_logo_w.svg',
};

const GMAPS_LIBRARIES: ('places' | 'geometry')[] = ['geometry'];

// ─── Map Component (Google Maps) ──────────────────────────────────
interface RoadRoute {
  id: string;
  path: Array<{ lat: number; lng: number }>;
  color: string;
  dashed: boolean;
  label: string;
  distance: number;
  minutes: number;
  midpoint: { lat: number; lng: number };
}

const DriverMap: React.FC<{
  orders: DeliveryOrder[];
  drivers: DriverInfo[];
  selectedOrderId?: string;
  onOrderClick: (order: DeliveryOrder) => void;
  flyToOrderId?: string;
  mapTheme: 'dark' | 'light';
  recenterToken?: number;
}> = ({ orders, drivers, selectedOrderId, onOrderClick, flyToOrderId, mapTheme, recenterToken }) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const initialFitDone = useRef(false);
  const [openInfo, setOpenInfo] = useState<{ type: 'branch' | 'order' | 'driver'; id: string } | null>(null);
  const [routes, setRoutes] = useState<RoadRoute[]>([]);

  // Inject CSS for driver pulse animation — declarative markers can't use Leaflet divIcons
  useEffect(() => {
    const styleId = 'driver-map-pulse-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes sk-driver-pulse-on {
          0%, 100% { box-shadow: 0 0 0 0 rgba(33,150,243,0.5); }
          50% { box-shadow: 0 0 0 12px rgba(33,150,243,0); }
        }
        @keyframes sk-driver-pulse-busy {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,152,0,0.5); }
          50% { box-shadow: 0 0 0 12px rgba(255,152,0,0); }
        }
        .gm-style-iw.gm-style-iw-c { padding: 12px !important; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Compute visible orders (with coordinates, not completed) — same filter used by markers
  const visibleOrders = useMemo(() => orders.filter(o =>
    o.delivery_latitude && o.delivery_longitude &&
    !isNaN(o.delivery_latitude) && !isNaN(o.delivery_longitude) &&
    !['delivered', 'completed', 'cancelled'].includes(o.status)
  ), [orders]);

  // Deduplicate online drivers by email
  const visibleDrivers = useMemo(() => {
    const seen = new Set<string>();
    return drivers.filter(d => {
      if (!d.latitude || !d.longitude || !d.is_active) return false;
      if (d.status !== 'available' && d.status !== 'online' && d.status !== 'busy') return false;
      if (seen.has(d.email)) return false;
      seen.add(d.email);
      return true;
    });
  }, [drivers]);

  // Assigned orders with coords (drives the route-drawing effect)
  const assignedOrders = useMemo(() => visibleOrders.filter(o => o.rider_email), [visibleOrders]);

  // Fetch OSRM road routes when assigned orders change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const accumulator: RoadRoute[] = [];

      // Restaurant → delivery (dashed) + driver → delivery (solid) for each assigned order
      for (const order of assignedOrders) {
        const restaurantPos: [number, number] = [BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude];
        const deliveryPos: [number, number] = [order.delivery_latitude!, order.delivery_longitude!];
        const assignedDriver = visibleDrivers.find(d => d.email === order.rider_email && d.latitude && d.longitude);

        // Route 1: Restaurant → Customer (dashed)
        const plannedColor = mapTheme === 'dark' ? '#00C896' : '#d32f2f';
        let plannedResult = await fetchOSRMRoute(restaurantPos, deliveryPos);
        let plannedPath = plannedResult?.coords ?? [
          { lat: restaurantPos[0], lng: restaurantPos[1] },
          { lat: deliveryPos[0], lng: deliveryPos[1] },
        ];
        const plannedDist = plannedResult?.distance ?? calculateDistance(restaurantPos[0], restaurantPos[1], deliveryPos[0], deliveryPos[1]);
        const plannedMin = plannedResult ? Math.round(plannedResult.duration) : Math.round(plannedDist * 2.5);
        accumulator.push({
          id: `planned-${order.id}`,
          path: plannedPath,
          color: plannedColor,
          dashed: true,
          label: '🏠', // delivery destination — customer's home
          distance: plannedDist,
          minutes: plannedMin,
          midpoint: plannedPath[Math.floor(plannedPath.length / 2)],
        });

        // Route 2: Driver → Customer (solid)
        if (assignedDriver) {
          const driverPos: [number, number] = [assignedDriver.latitude!, assignedDriver.longitude!];
          const activeColor = mapTheme === 'dark' ? '#2196F3' : '#0d47a1';
          let activeResult = await fetchOSRMRoute(driverPos, deliveryPos);
          let activePath = activeResult?.coords ?? [
            { lat: driverPos[0], lng: driverPos[1] },
            { lat: deliveryPos[0], lng: deliveryPos[1] },
          ];
          const activeDist = activeResult?.distance ?? calculateDistance(driverPos[0], driverPos[1], deliveryPos[0], deliveryPos[1]);
          const activeMin = activeResult ? Math.round(activeResult.duration) : Math.round(activeDist * 2.5);
          accumulator.push({
            id: `active-${order.id}`,
            path: activePath,
            color: activeColor,
            dashed: false,
            label: '🛵', // rider en route to the delivery destination
            distance: activeDist,
            minutes: activeMin,
            midpoint: activePath[Math.floor(activePath.length / 2)],
          });
        }
      }

      if (!cancelled) setRoutes(accumulator);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedOrders, visibleDrivers, mapTheme]);

  // Fit bounds: shared helper used both by initial load and the manual
  // "Recenter" button. NEVER auto-runs on data updates — that would yank
  // the map away from the user's manual zoom (the issue Owner reported).
  const fitAllToBounds = (map: google.maps.Map) => {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude });
    visibleOrders.forEach(o => bounds.extend({ lat: o.delivery_latitude!, lng: o.delivery_longitude! }));
    visibleDrivers.forEach(d => bounds.extend({ lat: d.latitude!, lng: d.longitude! }));
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 60);
      if (visibleOrders.length === 0 && visibleDrivers.length === 0) {
        map.setZoom(13);
      }
    }
  };

  // Fit bounds once after map + data are ready
  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (initialFitDone.current) return;
    fitAllToBounds(map);
    initialFitDone.current = true;
  };

  // Manual recenter — fired by the parent's "Fit all" button.
  // Skip the very first token (initial state) so we don't double-fit.
  const recenterTokenSeen = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (recenterToken === undefined) return;
    if (recenterTokenSeen.current === undefined) {
      recenterTokenSeen.current = recenterToken;
      return;
    }
    if (recenterToken !== recenterTokenSeen.current && mapRef.current) {
      fitAllToBounds(mapRef.current);
      recenterTokenSeen.current = recenterToken;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenterToken]);

  // Fly to a specific order when requested from the list — preserves the
  // user's current zoom unless they're already zoomed way out (e.g. panning
  // around the city). Avoids the old behaviour of forcing zoom 16 on every
  // click which was overriding manual zoom-out.
  useEffect(() => {
    if (!flyToOrderId || !mapRef.current) return;
    const order = orders.find(o => o.id === flyToOrderId);
    if (order?.delivery_latitude && order?.delivery_longitude) {
      mapRef.current.panTo({ lat: order.delivery_latitude, lng: order.delivery_longitude });
      const currentZoom = mapRef.current.getZoom() ?? 13;
      // Only zoom IN if the user is currently very zoomed out (< 13).
      // Otherwise respect their current zoom level.
      if (currentZoom < 13) {
        mapRef.current.setZoom(14);
      }
      setOpenInfo({ type: 'order', id: order.id });
    }
  }, [flyToOrderId, orders]);

  // Mark selected order InfoWindow open
  useEffect(() => {
    if (selectedOrderId) {
      setOpenInfo({ type: 'order', id: selectedOrderId });
    }
  }, [selectedOrderId]);

  const popupText = mapTheme === 'dark' ? '#000' : '#222';
  const popupSub = mapTheme === 'dark' ? '#444' : '#666';

  // Driver-to-branch distance lines (thin dashed) for online drivers not currently assigned
  const driverDistanceLines = useMemo(() => {
    return visibleDrivers
      .map(d => {
        const dist = calculateDistance(BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude, d.latitude!, d.longitude!);
        if (dist < 0.2) return null;
        return {
          id: `drvline-${d.id}`,
          from: { lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude },
          to: { lat: d.latitude!, lng: d.longitude! },
          distance: dist,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [visibleDrivers]);

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#1a1d2e', color: '#9ca3af', borderRadius: 1, p: 2 }}>
        <Typography variant="body2">🗺️ Google Maps API key missing — set REACT_APP_GOOGLE_MAPS_API_KEY</Typography>
      </Box>
    );
  }

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GMAPS_LIBRARIES}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%', minHeight: 400, borderRadius: 8 }}
        center={{ lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude }}
        zoom={13}
        onLoad={onMapLoad}
        options={{
          styles: mapTheme === 'dark' ? GMAPS_DARK_STYLE : GMAPS_LIGHT_STYLE,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          gestureHandling: 'greedy',
        }}
      >
        {/* Driver → branch dashed distance lines (thin grey) */}
        {driverDistanceLines.map(line => (
          <Polyline
            key={line.id}
            path={[line.from, line.to]}
            options={{
              strokeColor: '#9ca3af',
              strokeOpacity: 0,
              strokeWeight: 1,
              icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.5, scale: 3 }, offset: '0', repeat: '10px' }],
              clickable: false,
            }}
          />
        ))}

        {/* Routes (dashed planned + solid active) */}
        {routes.map(r => (
          <Polyline
            key={r.id}
            path={r.path}
            options={{
              strokeColor: r.color,
              strokeOpacity: r.dashed ? 0 : 0.85,
              strokeWeight: r.dashed ? 3 : 4,
              icons: r.dashed
                ? [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, scale: 3 }, offset: '0', repeat: '14px' }]
                : undefined,
              clickable: false,
            }}
          />
        ))}

        {/* Route midpoint badges (distance + ETA) */}
        {routes.map(r => (
          <OverlayView
            key={`badge-${r.id}`}
            position={r.midpoint}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
          >
            <div
              style={{
                background: mapTheme === 'dark' ? 'rgba(26,29,46,0.92)' : 'rgba(255,255,255,0.95)',
                color: r.color,
                padding: '4px 10px',
                borderRadius: 12,
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                border: `1px solid ${mapTheme === 'dark' ? '#3a3d52' : '#e0e0e0'}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                pointerEvents: 'none',
              }}
            >
              {r.label} {r.distance.toFixed(1)} km · ~{r.minutes} min
            </div>
          </OverlayView>
        ))}

        {/* Restaurant (branch) marker — logo in circle via OverlayView */}
        <OverlayView
          position={{ lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
        >
          <div
            onClick={() => setOpenInfo({ type: 'branch', id: 'branch' })}
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: mapTheme === 'dark' ? 'rgba(30,33,48,0.92)' : 'rgba(255,255,255,0.95)',
              border: `3px solid ${mapTheme === 'dark' ? '#555' : '#d32f2f'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              cursor: 'pointer',
              zIndex: 2000,
            }}
          >
            <img src={MAP_LOGO[mapTheme]} alt="" style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: '50%' }} />
          </div>
        </OverlayView>

        {openInfo?.type === 'branch' && (
          <InfoWindow
            position={{ lat: BRANCH_LOCATION.latitude, lng: BRANCH_LOCATION.longitude }}
            onCloseClick={() => setOpenInfo(null)}
          >
            <div style={{ color: popupText, fontFamily: 'sans-serif', minWidth: 160 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: popupText }}>{BRANCH_LOCATION.name}</div>
              <div style={{ color: popupSub, fontSize: 12 }}>
                {BRANCH_LOCATION.address}, {BRANCH_LOCATION.postcode} {BRANCH_LOCATION.city}
              </div>
            </div>
          </InfoWindow>
        )}

        {/* Order markers */}
        {visibleOrders.map(order => {
          const dist = calculateDistance(
            BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude,
            order.delivery_latitude!, order.delivery_longitude!
          );
          const icon = {
            url: orderIconSvg(order.trip_number || order.order_number, order.status),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 16),
          };
          return (
            <Marker
              key={order.id}
              position={{ lat: order.delivery_latitude!, lng: order.delivery_longitude! }}
              icon={icon}
              onClick={() => { onOrderClick(order); setOpenInfo({ type: 'order', id: order.id }); }}
            >
              {openInfo?.type === 'order' && openInfo.id === order.id && (
                <InfoWindow onCloseClick={() => setOpenInfo(null)}>
                  <div style={{ color: popupText, fontFamily: 'sans-serif', minWidth: 200 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: popupText }}>
                      #{order.order_number}
                      {order.trip_number && (
                        <span style={{ color: '#1976d2', fontWeight: 700, fontSize: 11, background: 'rgba(33,150,243,0.12)', padding: '1px 6px', borderRadius: 4, marginLeft: 6 }}>
                          DL {order.trip_number}
                        </span>
                      )}
                      {' — '}{order.customer_name}
                    </div>
                    <div style={{ color: popupSub, fontSize: 12 }}>📍 {order.delivery_address}</div>
                    <div style={{ color: popupSub, fontSize: 12 }}>💰 €{order.total.toFixed(2)} · 📏 {dist.toFixed(1)} km</div>
                    {order.rider_name
                      ? <div style={{ color: '#1976d2', fontSize: 12 }}>🚗 {order.rider_name}</div>
                      : <div style={{ color: '#e65100', fontSize: 12 }}>⚠ unassigned</div>}
                    <div style={{ color: statusColor(order.status), fontWeight: 600, fontSize: 12, marginTop: 2 }}>● {order.status}</div>
                  </div>
                </InfoWindow>
              )}
            </Marker>
          );
        })}

        {/* Driver markers — pulsing pill with the driver's name (per client request 2026-04-27) */}
        {visibleDrivers.map(driver => {
          const isBusy = driver.status === 'busy';
          // Resolve the driver's display name with a smart fallback chain:
          //   1. Real name from drivers/{id}.name (skip the literal "Unknown" placeholder)
          //   2. Email local-part (e.g. "rider1@saheehpos.com" → "Rider1")
          //   3. map_code if explicitly set
          //   4. "Driver" + last 3 chars of doc id
          // This prevents the pill from showing the unhelpful word "Unknown"
          // when admin hasn't filled in the rider's name yet.
          const cleanName = (driver.name || '').trim();
          const isPlaceholder = !cleanName || cleanName.toLowerCase() === 'unknown';
          const emailLocal = (driver.email || '').split('@')[0].trim();
          const idTail = driver.id ? driver.id.slice(-3).toUpperCase() : '';
          const fullName = !isPlaceholder
            ? cleanName
            : (emailLocal
                ? emailLocal.charAt(0).toUpperCase() + emailLocal.slice(1)
                : (driver.map_code || `Driver ${idTail}`).trim());
          const firstName = fullName.split(/\s+/)[0] || fullName;
          const label = firstName.length > 14 ? firstName.slice(0, 14) + '…' : firstName;
          const bg = isBusy ? '#FF9800' : '#2196F3';
          const width = 24 + label.length * 7.2;
          return (
            <React.Fragment key={driver.id}>
              <OverlayView
                position={{ lat: driver.latitude!, lng: driver.longitude! }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
              >
                <div
                  onClick={() => setOpenInfo({ type: 'driver', id: driver.id })}
                  style={{
                    minWidth: width,
                    height: 30,
                    padding: '0 12px',
                    borderRadius: 15,
                    background: bg,
                    border: '2px solid #fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '0.2px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    animation: `${isBusy ? 'sk-driver-pulse-busy' : 'sk-driver-pulse-on'} 2s ease-in-out infinite`,
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    zIndex: 1000,
                  }}
                >
                  {label}
                </div>
              </OverlayView>
              {openInfo?.type === 'driver' && openInfo.id === driver.id && (
                <InfoWindow
                  position={{ lat: driver.latitude!, lng: driver.longitude! }}
                  onCloseClick={() => setOpenInfo(null)}
                >
                  <div style={{ color: popupText, fontFamily: 'sans-serif', minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ background: bg, color: '#fff', fontWeight: 800, fontSize: 11, padding: '3px 10px', borderRadius: 12, letterSpacing: '0.5px' }}>{label}</span>
                      <b style={{ fontSize: 14, color: popupText }}>{driver.vehicle_type === 'bike' || driver.vehicle_type === 'bicycle' ? '🏍️' : '🚗'} {driver.name}</b>
                    </div>
                    <div style={{ color: isBusy ? '#FF9800' : '#4CAF50', fontSize: 11.5, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      ● {isBusy ? 'On Delivery' : driver.status === 'available' ? 'Online' : driver.status}
                    </div>
                    <div style={{ height: 1, background: '#eeeeee', margin: '6px 0 8px' }}></div>
                    {driver.phone && <div style={{ color: popupSub, fontSize: 12, margin: '3px 0' }}>📞 {driver.phone}</div>}
                    {driver.email && <div style={{ color: popupSub, fontSize: 12, margin: '3px 0' }}>✉️ {driver.email}</div>}
                    <div style={{ color: popupSub, fontSize: 12, margin: '3px 0' }}>
                      {driver.vehicle_type === 'bike' || driver.vehicle_type === 'bicycle' ? '🏍️' : '🚗'} {driver.vehicle_type || '—'}
                      {driver.license_plate ? ` · ${driver.license_plate}` : ''}
                    </div>
                    <div style={{ color: popupSub, fontSize: 12, margin: '3px 0' }}>🛵 {driver.deliveries_today} deliveries today</div>
                  </div>
                </InfoWindow>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </LoadScript>
  );
};

// ─── Main Component ──────────────────────────────────────────────
const DriverScreen: React.FC<{ defaultView?: string }> = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<DeliveryOrder[]>([]);
  const [drivers, setDrivers] = useState<DriverInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<DeliveryOrder | null>(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [driverPickerOpen, setDriverPickerOpen] = useState(false);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [highlightedOrderId, setHighlightedOrderId] = useState<string | undefined>();
  const [showDriverPanel, setShowDriverPanel] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unbooked' | 'active' | 'completed'>('all');
  const [mobileTab, setMobileTab] = useState<'orders' | 'map'>('orders');
  const [flyToOrderId, setFlyToOrderId] = useState<string | undefined>();
  // Increments every time the user hits "Recenter" — DriverMap watches this
  // and re-fits bounds. NEVER auto-increments on data updates.
  const [recenterToken, setRecenterToken] = useState(0);
  const [pickedDriverId, setPickedDriverId] = useState<string | null>(null);
  const [mapTheme, setMapTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('driverMapTheme') as 'dark' | 'light') || 'dark';
  });

  // ─── Stale Driver Cleanup (runs once on mount) ──────────────
  // Resets drivers that show "available" but haven't sent GPS in 5+ minutes
  // Stale-driver and stale-in-flight-order cleanup loops from the
  // legacy Firestore deployment were removed when the data layer moved
  // to Supabase. The same maintenance can live in a server-side cron
  // (`pg_cron` + a single SQL UPDATE) if/when staleness becomes an issue
  // again on a busy tenant.

  // ─── Supabase Realtime Listeners ─────────────────────────────
  useEffect(() => {
    const startOfDay = new Date();
    startOfDay.setHours(6, 0, 0, 0);
    if (new Date().getHours() < 6) startOfDay.setDate(startOfDay.getDate() - 1);

    let cancelled = false;

    const reloadOrders = async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_number, customer_snapshot, delivery_address_snapshot, type, status, grand_total, notes, payment_method, driver_id, placed_at, created_at, order_items(menu_item_snapshot, quantity)')
        .eq('type', 'delivery')
        .gte('created_at', startOfDay.toISOString())
        .order('created_at', { ascending: false });
      if (cancelled || error) return;
      const list: DeliveryOrder[] = (data ?? []).map((row: any) => {
        const customer = row.customer_snapshot || {};
        const address = row.delivery_address_snapshot || {};
        return {
          id: row.id,
          order_number: row.order_number || 0,
          customer_name: customer.name || 'Customer',
          delivery_address: address.address || customer.address || '',
          phone: customer.phone || '',
          total: Number(row.grand_total) || 0,
          item_count: (row.order_items ?? []).length,
          status: row.status || 'pending',
          rider_email: '',
          rider_name: '',
          created_at: new Date(row.placed_at || row.created_at),
          items: (row.order_items ?? []).map((it: any) => ({
            name: it.menu_item_snapshot?.name || 'Item',
            quantity: it.quantity || 1,
          })),
          payment_method: row.payment_method || 'cash',
          notes: row.notes || '',
          delivery_latitude: address.latitude,
          delivery_longitude: address.longitude,
          platform: '',
          customer_email: customer.email || '',
          customer_uid: '',
          is_guest: true,
          source: '',
          created_by: '',
        } as DeliveryOrder;
      });
      setOrders(assignTripNumbers(list));
      setLoading(false);
    };

    const reloadDrivers = async () => {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, name, map_code, email, phone, vehicle_type, license_plate, status, is_available, is_active, current_latitude, current_longitude, latitude, longitude, deliveries_today, total_deliveries, rating');
      if (cancelled || error) return;
      const list: DriverInfo[] = (data ?? []).map((d: any) => {
        const isOnline = d.status === 'available' || d.status === 'online' || d.status === 'busy';
        const rawName = d.name || d.email || 'Driver';
        const mapCode = (d.map_code || rawName).toString().trim().substring(0, 4).toUpperCase();
        return {
          id: d.id, name: rawName, map_code: mapCode,
          email: d.email || '', phone: d.phone || '',
          vehicle_type: d.vehicle_type || 'car',
          license_plate: d.license_plate || '',
          status: d.status || 'offline', is_available: d.is_available ?? false,
          is_active: d.is_active ?? true,
          latitude: isOnline ? (d.current_latitude || d.latitude) : undefined,
          longitude: isOnline ? (d.current_longitude || d.longitude) : undefined,
          deliveries_today: d.deliveries_today || 0,
          total_deliveries: d.total_deliveries || 0, rating: d.rating || 5.0,
        } as DriverInfo;
      });
      setDrivers(list);
    };

    void reloadOrders();
    void reloadDrivers();

    const ordersCh = supabase
      .channel('drv_orders')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => { void reloadOrders(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items' }, () => { void reloadOrders(); })
      .subscribe();

    const driversCh = supabase
      .channel('drv_drivers')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'drivers' }, () => { void reloadDrivers(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'driver_locations' }, () => { void reloadDrivers(); })
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(ordersCh);
      void supabase.removeChannel(driversCh);
    };
  }, []);

  // ─── Re-geocode orders with missing or imprecise coordinates ──
  // Runs when orders change; geocodes delivery_address → lat/lng using Nominatim structured queries
  // Updates both local state AND Firestore so the fix is permanent
  const geocodedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const regeocodeOrders = async () => {
      for (const order of orders) {
        // Skip if already geocoded in this session, or no address to geocode
        if (geocodedRef.current.has(order.id)) continue;
        if (!order.delivery_address || order.delivery_address.trim().length < 3) continue;
        // Skip completed/cancelled orders
        if (['delivered', 'completed', 'cancelled'].includes(order.status)) continue;

        // Mark as attempted so we don't retry
        geocodedRef.current.add(order.id);

        // Parse address into parts: "street, postcode city" or "street, city"
        const addr = order.delivery_address.trim();
        let street = addr;
        let postcode = '';
        let city = '';

        // Try to extract postcode and city from address like "Grabenstr 11, 50354 Hürth"
        const match = addr.match(/^(.+?),\s*(\d{5})\s+(.+)$/);
        if (match) {
          street = match[1].trim();
          postcode = match[2];
          city = match[3].trim();
        } else {
          // Try "street, city" format
          const match2 = addr.match(/^(.+?),\s*(.+)$/);
          if (match2) {
            street = match2[1].trim();
            city = match2[2].trim();
            // Check if city starts with postcode
            const pcMatch = city.match(/^(\d{5})\s+(.+)$/);
            if (pcMatch) {
              postcode = pcMatch[1];
              city = pcMatch[2].trim();
            }
          }
        }

        // Default to Hürth area if no city found
        if (!city) city = 'Hürth';
        if (!postcode) postcode = '50354';

        // Build queries: street-level first (most precise), then fallback
        const queries: Array<{ params: string; label: string }> = [];
        if (street && street !== addr) {
          queries.push({
            params: `street=${encodeURIComponent(street)}&postalcode=${encodeURIComponent(postcode)}&city=${encodeURIComponent(city)}&country=Germany`,
            label: `Structured: ${street}, ${postcode} ${city}`,
          });
          queries.push({
            params: `q=${encodeURIComponent(`${street}, ${postcode} ${city}, Germany`)}`,
            label: `Free-form: ${street}, ${postcode} ${city}`,
          });
        }
        queries.push({
          params: `q=${encodeURIComponent(`${addr}, Germany`)}`,
          label: `Full address: ${addr}`,
        });

        let bestLat: number | undefined;
        let bestLon: number | undefined;

        for (const { params, label } of queries) {
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/search?${params}&format=json&limit=1&countrycodes=de&addressdetails=1`,
              { headers: { 'User-Agent': 'SushiPOS/1.0' } }
            );
            if (!resp.ok) continue;
            const data = await resp.json();
            if (!data || data.length === 0) continue;

            const result = data[0];
            const lat = parseFloat(result.lat);
            const lon = parseFloat(result.lon);
            if (isNaN(lat) || isNaN(lon)) continue;

            const isStreetLevel = ['house', 'building', 'residential', 'road', 'street', 'address'].some(
              t => (result.type || '').includes(t) || (result.class || '').includes(t)
            );

            if (isStreetLevel) {
              bestLat = lat;
              bestLon = lon;
              console.log(`[RE-GEOCODE] #${order.order_number} "${addr}" → STREET-LEVEL: ${lat},${lon} (${label})`);
              break;
            } else if (bestLat === undefined) {
              bestLat = lat;
              bestLon = lon;
            }
          } catch (e) {
            // continue to next query
          }
          // Rate limit: 100ms between Nominatim requests
          await new Promise(r => setTimeout(r, 100));
        }

        if (bestLat !== undefined && bestLon !== undefined) {
          // Check if coordinates actually changed significantly (> ~50 meters)
          const oldLat = order.delivery_latitude || 0;
          const oldLon = order.delivery_longitude || 0;
          const moved = Math.abs(bestLat - oldLat) > 0.0005 || Math.abs(bestLon - oldLon) > 0.0005;

          if (moved || !order.delivery_latitude || !order.delivery_longitude) {
            console.log(`[RE-GEOCODE] #${order.order_number} updating: (${oldLat},${oldLon}) → (${bestLat},${bestLon})`);
            // Update local state
            setOrders(prev => prev.map(o =>
              o.id === order.id ? { ...o, delivery_latitude: bestLat, delivery_longitude: bestLon } : o
            ));
            // Persist to Supabase so it's permanent.
            try {
              const { error } = await supabase
                .from('orders')
                .update({
                  delivery_address_snapshot: {
                    ...((selectedOrder as any)?.delivery_address_snapshot || {}),
                    latitude: bestLat,
                    longitude: bestLon,
                  },
                })
                .eq('id', order.id);
              if (error) throw error;
            } catch (e) {
              console.warn(`[RE-GEOCODE] Failed to save #${order.order_number} to Supabase:`, e);
            }
          }
        }

        // Rate limit between orders
        await new Promise(r => setTimeout(r, 200));
      }
    };

    if (orders.length > 0) {
      regeocodeOrders();
    }
  }, [orders]);

  // ─── Actions ────────────────────────────────────────────────
  const assignDriver = async (driver: DriverInfo) => {
    if (!selectedOrder) return;
    try {
      const { error } = await supabase.from('orders').update({
        driver_id: driver.id,
        status: 'assigned',
        assigned_at: new Date().toISOString(),
      }).eq('id', selectedOrder.id);
      if (error) throw error;
      // Mark the driver as busy.
      await supabase.from('drivers').update({ status: 'busy', is_available: false }).eq('id', driver.id);
      setDriverPickerOpen(false); setActionDialog(false); setSelectedOrder(null);
    } catch (e) { console.error('Assign error:', e); }
  };

  /// Reassign order to a different driver (admin only) — frees the old driver.
  const reassignDriver = async (newDriver: DriverInfo) => {
    if (!selectedOrder) return;
    try {
      const prevDriverId = (selectedOrder as any).driver_id;
      if (prevDriverId) {
        await supabase.from('drivers').update({ status: 'available', is_available: true }).eq('id', prevDriverId);
      }
      const { error } = await supabase.from('orders').update({
        driver_id: newDriver.id,
        status: 'assigned',
        assigned_at: new Date().toISOString(),
      }).eq('id', selectedOrder.id);
      if (error) throw error;
      await supabase.from('drivers').update({ status: 'busy', is_available: false }).eq('id', newDriver.id);
      setDriverPickerOpen(false); setActionDialog(false); setSelectedOrder(null);
    } catch (e) { console.error('Reassign error:', e); }
  };

  const completeOrder = async (order: DeliveryOrder) => {
    try {
      const { error } = await supabase.from('orders').update({
        status: 'delivered',
        payment_status: 'completed',
        delivered_at: new Date().toISOString(),
      }).eq('id', order.id);
      if (error) throw error;
      const driverId = (order as any).driver_id;
      if (driverId) {
        await supabase.from('drivers').update({ status: 'available', is_available: true }).eq('id', driverId);
      }
      setActionDialog(false);
    } catch (e) { console.error('Complete error:', e); }
  };

  /// Cancel order (admin only) — also frees the assigned driver.
  const cancelOrder = async (order: DeliveryOrder) => {
    try {
      const { error } = await supabase.from('orders').update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancellation_reason: 'admin_cancel',
      }).eq('id', order.id);
      if (error) throw error;
      const driverId = (order as any).driver_id;
      if (driverId) {
        await supabase.from('drivers').update({ status: 'available', is_available: true }).eq('id', driverId);
      }
      setActionDialog(false);
    } catch (e) { console.error('Cancel error:', e); }
  };

  // Track if driver picker is for reassignment vs new assignment
  const [isReassigning, setIsReassigning] = useState(false);

  // ─── Computed ───────────────────────────────────────────────
  // Enrich orders with LIVE driver name from drivers collection (handles admin renames)
  // Active orders always show current driver name; completed orders keep their historical name
  const enrichedOrders = useMemo(() => {
    const driverByEmail = new Map<string, DriverInfo>();
    drivers.forEach(d => { if (d.email) driverByEmail.set(d.email.toLowerCase(), d); });
    return orders.map(o => {
      if (!o.rider_email) return o;
      const liveDriver = driverByEmail.get(o.rider_email.toLowerCase());
      if (!liveDriver) return o;
      // For active (non-completed) orders, use live name; for completed, keep snapshot
      const isActive = !['delivered', 'completed', 'cancelled'].includes(o.status);
      return isActive
        ? { ...o, rider_name: liveDriver.name || o.rider_name }
        : o;
    });
  }, [orders, drivers]);

  const unbooked = enrichedOrders.filter(o => !o.rider_email && !['delivered', 'completed', 'cancelled'].includes(o.status));
  const onlineDrivers = drivers.filter(d => d.status === 'available' || d.status === 'online');
  const ordersWithCoords = enrichedOrders.filter(o => o.delivery_latitude && o.delivery_longitude);

  const filteredOrders = useMemo(() => {
    switch (filter) {
      case 'unbooked': return enrichedOrders.filter(o => !o.rider_email && !['delivered', 'completed', 'cancelled'].includes(o.status));
      case 'active': return enrichedOrders.filter(o => ['assigned', 'picked_up', 'in_transit', 'on_delivery'].includes(o.status));
      case 'completed': return enrichedOrders.filter(o => ['delivered', 'completed'].includes(o.status));
      default: return enrichedOrders;
    }
  }, [enrichedOrders, filter]);

  const handleMapOrderClick = React.useCallback((order: DeliveryOrder) => {
    setSelectedOrder(order);
    setActionDialog(true);
  }, []);

  // ─── Render ─────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', minHeight: 0, backgroundColor: '#1a1d2e', overflow: 'hidden' }}>

      {/* ── Header Bar ──────────────────────────────────────── */}
      <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 0.8, md: 1.2 }, flexShrink: 0, borderBottom: '1px solid #3a3d52', backgroundColor: '#1e2139' }}>
        {/* Desktop header */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip icon={<CalendarToday sx={{ fontSize: 14 }} />} label={dateStr}
              sx={{ backgroundColor: '#2196F3', color: '#fff', fontWeight: 600, fontSize: '0.78rem', '& .MuiChip-icon': { color: '#fff' } }} />
            {unbooked.length > 0 && (
              <Button variant="contained" startIcon={<AssignmentInd sx={{ fontSize: 16 }} />} size="small"
                onClick={() => { setSelectedOrder(unbooked[0]); setDriverPickerOpen(true); }}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: 2.5, fontSize: '0.78rem',
                  background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                  color: '#fff', px: 2, py: 0.7,
                  boxShadow: '0 2px 8px rgba(255,107,53,0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #E55A2B 0%, #E08516 100%)', boxShadow: '0 4px 12px rgba(255,107,53,0.5)' },
                }}>
                {t('drivers.book_on_driver')}
                <Box sx={{
                  ml: 1, minWidth: 20, height: 20, borderRadius: '50%',
                  backgroundColor: '#fff', color: '#FF6B35',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 800, lineHeight: 1,
                }}>{unbooked.length}</Box>
              </Button>
            )}
            <Button variant={showDriverPanel ? 'contained' : 'outlined'} startIcon={<DeliveryDining />} size="small"
              onClick={() => setShowDriverPanel(!showDriverPanel)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.75rem',
                borderColor: '#3a3d52', color: showDriverPanel ? '#fff' : '#9ca3af',
                backgroundColor: showDriverPanel ? '#2196F3' : 'transparent',
                '&:hover': { backgroundColor: showDriverPanel ? '#1976D2' : 'rgba(255,255,255,0.05)' } }}>
              {t('drivers.drivers_label', { online: String(onlineDrivers.length), total: String(drivers.length) })}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip icon={<LocalShipping sx={{ fontSize: 14 }} />} label={t('drivers.orders_count', { count: String(orders.length) })} size="small"
              sx={{ backgroundColor: '#252838', border: '1px solid #3a3d52', color: '#e0e0e0', fontSize: '0.72rem' }} />
            <Chip icon={<LocationOn sx={{ fontSize: 14 }} />} label={t('drivers.on_map', { count: String(ordersWithCoords.length) })} size="small"
              sx={{ backgroundColor: '#252838', border: '1px solid #3a3d52', color: ordersWithCoords.length > 0 ? '#00C896' : '#6b7280', fontSize: '0.72rem' }} />
            <IconButton size="small" sx={{ color: '#9ca3af', border: '1px solid #3a3d52', borderRadius: 1.5 }}
              onClick={() => window.location.reload()}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile header — compact with tab switcher */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Chip icon={<CalendarToday sx={{ fontSize: 12 }} />} label={dateStr} size="small"
                sx={{ backgroundColor: '#2196F3', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 26, '& .MuiChip-icon': { color: '#fff' } }} />
              <Chip label={`${orders.length}`} size="small" icon={<LocalShipping sx={{ fontSize: 12 }} />}
                sx={{ backgroundColor: '#252838', border: '1px solid #3a3d52', color: '#e0e0e0', fontSize: '0.68rem', height: 26 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {unbooked.length > 0 && (
                <Button variant="contained" size="small"
                  onClick={() => { setSelectedOrder(unbooked[0]); setDriverPickerOpen(true); }}
                  sx={{
                    textTransform: 'none', fontWeight: 800, borderRadius: 2, fontSize: '0.75rem', minWidth: 0, px: 1.5, py: 0.5,
                    background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                    boxShadow: '0 2px 6px rgba(255,107,53,0.3)',
                    '&:hover': { background: 'linear-gradient(135deg, #E55A2B 0%, #E08516 100%)' },
                  }}>
                  <AssignmentInd sx={{ fontSize: 14, mr: 0.3 }} />{unbooked.length}
                </Button>
              )}
              <IconButton size="small" sx={{ color: '#9ca3af', border: '1px solid #3a3d52', borderRadius: 1.5, p: 0.5 }}
                onClick={() => window.location.reload()}>
                <Refresh sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
          {/* Mobile tab switcher */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {([
              { key: 'orders' as const, icon: <LocalShipping sx={{ fontSize: 14 }} />, label: `${t('drivers.filter_all')} (${orders.length})` },
              { key: 'map' as const, icon: <LocationOn sx={{ fontSize: 14 }} />, label: t('drivers.legend_driver') },
            ]).map(tab => (
              <Button key={tab.key} startIcon={tab.icon} size="small"
                onClick={() => setMobileTab(tab.key)}
                sx={{
                  flex: 1, textTransform: 'none', fontWeight: 600, fontSize: '0.72rem', borderRadius: 1.5, py: 0.6,
                  backgroundColor: mobileTab === tab.key ? '#2196F3' : '#252838',
                  color: mobileTab === tab.key ? '#fff' : '#9ca3af',
                  border: mobileTab === tab.key ? 'none' : '1px solid #3a3d52',
                }}>
                {tab.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Main Content: Split View (desktop) / Tabs (mobile) ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden', minHeight: 0 }}>

        {/* LEFT: Order List */}
        <Box sx={{
          width: { xs: '100%', md: showDriverPanel ? '35%' : '45%' },
          minWidth: { md: 320 }, maxWidth: { md: 580 },
          display: { xs: mobileTab === 'orders' ? 'flex' : 'none', md: 'flex' },
          flexDirection: 'column',
          borderRight: { md: '1px solid #3a3d52' },
          height: { xs: '100%', md: '100%' },
        }}>
          {/* Unbooked orders checkbox + filter tabs (Liefersoft-style) */}
          <Box sx={{ px: 1.5, py: 0.8, display: 'flex', gap: 0.8, borderBottom: '1px solid #2a2d3e', backgroundColor: '#1e2139', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Unbooked checkbox — like Liefersoft */}
            <Box
              onClick={() => setFilter(filter === 'unbooked' ? 'all' : 'unbooked')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer',
                backgroundColor: filter === 'unbooked' ? 'rgba(76,175,80,0.08)' : 'transparent',
                border: filter === 'unbooked' ? '1px solid rgba(76,175,80,0.3)' : '1px solid #3a3d52',
                borderRadius: 2, px: 1.2, py: 0.5,
                '&:hover': { borderColor: '#4CAF50' },
                transition: 'all 0.15s',
              }}>
              <Box sx={{
                width: 18, height: 18, borderRadius: 0.8,
                border: filter === 'unbooked' ? '2px solid #4CAF50' : '2px solid #4a4d62',
                backgroundColor: filter === 'unbooked' ? '#4CAF50' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}>
                {filter === 'unbooked' && <CheckCircle sx={{ fontSize: 14, color: '#fff' }} />}
              </Box>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: filter === 'unbooked' ? '#e0e0e0' : '#9ca3af' }}>
                Unbooked orders
              </Typography>
            </Box>
            {/* Quick filter chips */}
            {(['all', 'active', 'completed'] as const).map(f => (
              <Chip key={f} label={f === 'all' ? t('drivers.filter_all') : f === 'active' ? t('drivers.filter_active') : t('drivers.filter_done')}
                size="small" onClick={() => setFilter(f)}
                sx={{
                  height: 24, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: filter === f ? '#2196F3' : '#252838',
                  color: filter === f ? '#fff' : '#9ca3af',
                  border: filter === f ? 'none' : '1px solid #3a3d52',
                  '&:hover': { backgroundColor: filter === f ? '#1976D2' : '#2a2d42' },
                }} />
            ))}
          </Box>

          {/* Column header */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, px: 1.5, py: 0.6, gap: 1, borderBottom: '1px solid #2a2d3e', backgroundColor: '#1a1d2e', flexShrink: 0 }}>
            <Typography sx={{ width: 35, fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', flexShrink: 0 }}>{t('drivers.nr')}</Typography>
            <Typography sx={{ width: 45, fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', flexShrink: 0 }}>{t('drivers.time')}</Typography>
            <Typography sx={{ flex: 1, fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', minWidth: 0 }}>{t('drivers.customer_platform_sum')}</Typography>
            <Typography sx={{ width: 65, fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', flexShrink: 0 }}>{t('drivers.order_type')}</Typography>
            <Typography sx={{ width: 85, fontSize: '0.65rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', flexShrink: 0 }}>{t('drivers.status')}</Typography>
          </Box>

          {/* Order rows */}
          <Box sx={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#00C896' }} size={32} /></Box>
            ) : filteredOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <LocalShipping sx={{ fontSize: 48, color: '#3a3d52', mb: 1 }} />
                <Typography sx={{ color: '#6b7280', fontSize: '0.85rem', fontWeight: 500 }}>{t('drivers.no_delivery_orders')}</Typography>
                <Typography sx={{ color: '#4a4d62', fontSize: '0.72rem', mt: 0.5 }}>{t('drivers.orders_appear_here')}</Typography>
              </Box>
            ) : (
              filteredOrders.map((order) => {
                const dist = (order.delivery_latitude && order.delivery_longitude)
                  ? calculateDistance(BRANCH_LOCATION.latitude, BRANCH_LOCATION.longitude, order.delivery_latitude, order.delivery_longitude)
                  : null;
                const isNew = !order.rider_email && !['delivered', 'completed', 'cancelled'].includes(order.status);
                const isHighlighted = highlightedOrderId === order.id;

                return (
                  <React.Fragment key={order.id}>
                    {/* Desktop row */}
                    <Box onClick={() => { setSelectedOrder(order); setActionDialog(true); setFlyToOrderId(order.id); }}
                      onMouseEnter={() => setHighlightedOrderId(order.id)}
                      onMouseLeave={() => setHighlightedOrderId(undefined)}
                      sx={{
                        display: { xs: 'none', md: 'flex' }, px: 1.5, py: 1.2, gap: 1, cursor: 'pointer',
                        borderBottom: '1px solid #2a2d3e', alignItems: 'flex-start',
                        borderLeft: `3px solid ${statusColor(order.status)}`,
                        backgroundColor: isHighlighted ? 'rgba(0, 200, 150,0.06)' : 'transparent',
                        '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.06)' },
                        transition: 'background-color 0.15s',
                      }}>
                      <Box sx={{ width: 50, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#e0e0e0', lineHeight: 1.2 }}>{order.order_number}</Typography>
                        {order.trip_number && (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, backgroundColor: 'rgba(33,150,243,0.12)', border: '1px solid rgba(33,150,243,0.25)', borderRadius: 1, px: 0.5, py: 0.1 }}>
                            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#64B5F6', lineHeight: 1 }}>T{order.trip_number}</Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ width: 45, fontSize: '0.8rem', fontWeight: 600, color: '#9ca3af', flexShrink: 0 }}>{fmtTime(order.created_at)}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#e0e0e0' }} noWrap>{order.customer_name}</Typography>
                          {isNew && <Chip label={t('drivers.status_new').toUpperCase()} size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, backgroundColor: '#FF9800', color: '#fff', ml: 0.3 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }} noWrap>{order.delivery_address}</Typography>
                          {dist !== null && (
                            <Chip label={`${dist} km`} size="small"
                              sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, backgroundColor: 'rgba(33,150,243,0.15)', color: '#64B5F6', ml: 0.3, flexShrink: 0 }} />
                          )}
                        </Box>
                        {order.platform && (
                          <Chip icon={<span style={{ fontSize: '0.6rem' }}>🏪</span>} label={order.platform} size="small"
                            sx={{ height: 16, fontSize: '0.55rem', fontWeight: 600, backgroundColor: 'rgba(76,175,80,0.12)', color: '#81C784', mt: 0.3 }} />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                          {order.rider_name ? (
                            <Chip
                              avatar={<Avatar sx={{ width: 14, height: 14, fontSize: '0.5rem', bgcolor: '#2196F3' }}>{order.rider_name.charAt(0)}</Avatar>}
                              label={order.rider_name.split(' ')[0]} size="small"
                              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, backgroundColor: '#252838', border: '1px solid #3a3d52', color: '#e0e0e0' }} />
                          ) : null}
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#00C896' }}>€{order.total.toFixed(2)}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ width: 65, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Chip icon={<LocalShipping sx={{ fontSize: 12 }} />} label={t('drivers.delivery')} size="small"
                          sx={{ height: 20, fontSize: '0.5rem', fontWeight: 600, backgroundColor: 'rgba(0,188,212,0.12)', color: '#00BCD4', '& .MuiChip-icon': { color: '#00BCD4' } }} />
                      </Box>
                      <Box sx={{ width: 85, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Chip
                          icon={order.status === 'delivered' || order.status === 'completed'
                            ? <CheckCircle sx={{ fontSize: 12 }} />
                            : order.status === 'in_transit' || order.status === 'on_delivery'
                            ? <DeliveryDining sx={{ fontSize: 12 }} />
                            : undefined}
                          label={t(statusKey(order.status))} size="small"
                          sx={{
                            height: 22, fontSize: '0.58rem', fontWeight: 600,
                            backgroundColor: `${statusColor(order.status)}18`, color: statusColor(order.status),
                            border: `1px solid ${statusColor(order.status)}30`,
                            '& .MuiChip-icon': { color: statusColor(order.status) },
                          }} />
                      </Box>
                    </Box>

                    {/* Mobile card — larger touch targets */}
                    <Box onClick={() => { setSelectedOrder(order); setActionDialog(true); setFlyToOrderId(order.id); }} sx={{
                      display: { xs: 'flex', md: 'none' }, flexDirection: 'column', mx: 1.5, my: 0.8,
                      p: 1.5, borderRadius: 2.5, backgroundColor: '#252838', border: '1px solid #3a3d52',
                      borderLeft: `4px solid ${statusColor(order.status)}`, cursor: 'pointer',
                      minHeight: 80, // ensure good touch target
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#e0e0e0', fontFamily: 'monospace' }}>#{order.order_number}</Typography>
                          {order.trip_number && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(33,150,243,0.15)', border: '1px solid rgba(33,150,243,0.3)', borderRadius: 1.5, px: 0.8, py: 0.2 }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#64B5F6', lineHeight: 1 }}>Trip {order.trip_number}</Typography>
                            </Box>
                          )}
                          {isNew && <Chip label={t('drivers.status_new').toUpperCase()} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 800, backgroundColor: '#FF9800', color: '#fff' }} />}
                          <Chip label={t(statusKey(order.status))} size="small" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600,
                            backgroundColor: `${statusColor(order.status)}15`, color: statusColor(order.status) }} />
                        </Box>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#00C896' }}>€{order.total.toFixed(2)}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 500, color: '#e0e0e0' }}>{order.customer_name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                        <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', flex: 1 }} noWrap>{order.delivery_address}</Typography>
                        {dist !== null && <Chip label={`${dist} km`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, backgroundColor: 'rgba(33,150,243,0.15)', color: '#64B5F6' }} />}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>{fmtTime(order.created_at)} · {order.item_count} {t('drivers.delivery')}</Typography>
                        {order.rider_name && <Chip label={order.rider_name.split(' ')[0]} size="small"
                          sx={{ height: 22, fontSize: '0.65rem', backgroundColor: '#1e2139', border: '1px solid #3a3d52', color: '#9ca3af' }} />}
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })
            )}
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box sx={{
          flex: 1, position: 'relative',
          display: { xs: mobileTab === 'map' ? 'block' : 'none', md: 'block' },
          height: { xs: '100%', md: '100%' },
          minHeight: { xs: 0, md: 300 }, minWidth: 0,
        }}>
          <MapErrorBoundary>
            <DriverMap
              orders={enrichedOrders}
              drivers={drivers}
              selectedOrderId={highlightedOrderId}
              onOrderClick={handleMapOrderClick}
              flyToOrderId={flyToOrderId}
              mapTheme={mapTheme}
              recenterToken={recenterToken}
            />
          </MapErrorBoundary>

          {/* Map legend overlay — uses plain spans to avoid MUI dark theme color override */}
          {(() => {
            const legendColor = mapTheme === 'dark' ? '#9ca3af' : '#2e7d32';
            const legendBg = mapTheme === 'dark' ? 'rgba(26,29,46,0.92)' : 'rgba(255,255,255,0.95)';
            const legendBorder = mapTheme === 'dark' ? '#3a3d52' : '#d0d0d0';
            return (
              <div style={{
                position: 'absolute', bottom: 12, left: 12,
                backgroundColor: legendBg, borderRadius: 8, padding: '8px 12px',
                border: `1px solid ${legendBorder}`, zIndex: 1000,
                boxShadow: mapTheme === 'light' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <img src={MAP_LOGO[mapTheme]} alt="R" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'contain' }} />
                  <span style={{ fontSize: '0.65rem', color: legendColor, fontWeight: 500 }}>{t('drivers.legend_restaurant')}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#FF9800', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontWeight: 700, color: '#fff' }}>#</span>
                  <span style={{ fontSize: '0.65rem', color: legendColor, fontWeight: 500 }}>{t('drivers.legend_new_order')}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#2196F3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontWeight: 700, color: '#fff' }}>F</span>
                  <span style={{ fontSize: '0.65rem', color: legendColor, fontWeight: 500 }}>{t('drivers.legend_driver')}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', fontWeight: 700, color: '#fff' }}>✓</span>
                  <span style={{ fontSize: '0.65rem', color: legendColor, fontWeight: 500 }}>{t('drivers.legend_completed')}</span>
                </span>
              </div>
            );
          })()}

          {/* Top-right overlays: recenter, theme toggle, driver count */}
          <Box sx={{
            position: 'absolute', top: 12, right: 12, display: 'flex', gap: 0.5, zIndex: 1000,
          }}>
            {/* Recenter / Fit-all button — re-fits the view to branch + drivers + orders */}
            <Chip
              label="⤢"
              size="small"
              title="Fit map to drivers and orders"
              onClick={() => setRecenterToken((n) => n + 1)}
              sx={{
                backgroundColor: mapTheme === 'dark' ? 'rgba(26,29,46,0.92)' : 'rgba(255,255,255,0.95)',
                border: `1px solid ${mapTheme === 'dark' ? '#3a3d52' : '#d0d0d0'}`,
                color: mapTheme === 'dark' ? '#e0e0e0' : '#333',
                fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
                boxShadow: mapTheme === 'light' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                '&:hover': { backgroundColor: mapTheme === 'dark' ? 'rgba(40,43,60,0.95)' : 'rgba(240,240,240,0.98)' },
              }}
            />
            {/* Map theme toggle */}
            <Chip
              label={mapTheme === 'dark' ? '☀️' : '🌙'}
              size="small"
              onClick={() => {
                const next = mapTheme === 'dark' ? 'light' : 'dark';
                setMapTheme(next);
                localStorage.setItem('driverMapTheme', next);
              }}
              sx={{
                backgroundColor: mapTheme === 'dark' ? 'rgba(26,29,46,0.92)' : 'rgba(255,255,255,0.95)',
                border: `1px solid ${mapTheme === 'dark' ? '#3a3d52' : '#d0d0d0'}`,
                color: mapTheme === 'dark' ? '#e0e0e0' : '#333',
                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                boxShadow: mapTheme === 'light' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                '&:hover': { backgroundColor: mapTheme === 'dark' ? 'rgba(40,43,60,0.95)' : 'rgba(240,240,240,0.98)' },
              }}
            />
            <Chip label={t('drivers.drivers_online', { count: String(onlineDrivers.length) })}
              icon={<FiberManualRecord sx={{ fontSize: 10, color: onlineDrivers.length > 0 ? '#4CAF50' : '#F44336' }} />}
              size="small"
              sx={{
                backgroundColor: mapTheme === 'dark' ? 'rgba(26,29,46,0.92)' : 'rgba(255,255,255,0.95)',
                border: `1px solid ${mapTheme === 'dark' ? '#3a3d52' : '#d0d0d0'}`,
                color: mapTheme === 'dark' ? '#e0e0e0' : '#333',
                fontSize: '0.7rem', fontWeight: 600,
                boxShadow: mapTheme === 'light' ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              }} />
          </Box>
        </Box>

        {/* RIGHT PANEL: Drivers (toggle) */}
        {showDriverPanel && (
          <Box sx={{
            width: { xs: '100%', md: 260 }, flexShrink: 0,
            borderLeft: { md: '1px solid #3a3d52' },
            display: 'flex', flexDirection: 'column',
            backgroundColor: '#1e2139', overflow: 'auto',
          }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #2a2d3e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#e0e0e0' }}>{t('drivers.drivers_label', { online: String(onlineDrivers.length), total: String(drivers.length) })}</Typography>
              <IconButton size="small" onClick={() => setShowDriverPanel(false)} sx={{ color: '#6b7280' }}><Close sx={{ fontSize: 16 }} /></IconButton>
            </Box>
            {drivers.filter(d => d.is_active).map((driver) => {
              const assignedOrders = orders.filter(o => o.rider_email === driver.email && !['delivered', 'completed', 'cancelled'].includes(o.status));
              return (
                <Box key={driver.id} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #2a2d3e', '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.68rem', fontWeight: 800,
                      backgroundColor: driver.status === 'available' ? '#4CAF50' : driver.status === 'busy' ? '#FF9800' : '#3a3d52',
                      color: '#fff', letterSpacing: '0.5px' }}>
                      {driver.map_code || driver.name.substring(0, 4).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#e0e0e0' }} noWrap>{driver.name}</Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: driver.status === 'available' ? '#4CAF50' : '#F44336' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.62rem', color: '#6b7280' }}>
                        {driver.vehicle_type} · {assignedOrders.length} {t('drivers.active')} · {driver.deliveries_today} {t('drivers.today')}
                      </Typography>
                    </Box>
                  </Box>
                  {driver.latitude && driver.longitude && (
                    <Typography sx={{ fontSize: '0.58rem', color: '#4a4d62', mt: 0.3, ml: 4.5 }}>
                      GPS: {driver.latitude.toFixed(4)}, {driver.longitude.toFixed(4)}
                    </Typography>
                  )}
                </Box>
              );
            })}
            {drivers.filter(d => d.is_active).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DeliveryDining sx={{ fontSize: 36, color: '#3a3d52', mb: 0.5 }} />
                <Typography sx={{ color: '#6b7280', fontSize: '0.78rem' }}>{t('drivers.no_drivers_registered')}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* ─── Order Action Dialog ─────────────────────────────── */}
      <Dialog open={actionDialog} onClose={() => setActionDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#252838', border: '1px solid #3a3d52' } }}>
        {selectedOrder && (
          <>
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '1.1rem' }}>{t('drivers.order_number', { number: String(selectedOrder.order_number) })}</Typography>
                  {selectedOrder.trip_number && (
                    <Box sx={{ backgroundColor: 'rgba(33,150,243,0.15)', border: '1px solid rgba(33,150,243,0.3)', borderRadius: 1.5, px: 1, py: 0.2 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#64B5F6' }}>DL {selectedOrder.trip_number}</Typography>
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>{selectedOrder.customer_name} · {fmtTime(selectedOrder.created_at)}</Typography>
              </Box>
              <IconButton onClick={() => setActionDialog(false)} sx={{ color: '#6b7280' }}><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 0, py: 0 }}>
              {/* Quick info */}
              <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #2a2d3e', backgroundColor: '#1e2139' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <LocationOn sx={{ fontSize: 14, color: '#6b7280' }} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#e0e0e0' }}>{selectedOrder.delivery_address}</Typography>
                </Box>
                {selectedOrder.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Phone sx={{ fontSize: 14, color: '#6b7280' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#e0e0e0' }}>{selectedOrder.phone}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip label={t(statusKey(selectedOrder.status))} size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600,
                      backgroundColor: `${statusColor(selectedOrder.status)}18`, color: statusColor(selectedOrder.status) }} />
                  <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '1rem' }}>€{selectedOrder.total.toFixed(2)}</Typography>
                </Box>
              </Box>
              {/* Actions */}
              <List sx={{ py: 0 }}>
                {[
                  { label: t('drivers.show_order'), icon: <Visibility />, action: () => { setActionDialog(false); setOrderDetailOpen(true); } },
                  // Show "Book on Driver" if no driver assigned, "Reassign Driver" if already assigned
                  ...(!selectedOrder.rider_email ? [
                    { label: t('drivers.book_on_driver'), icon: <AssignmentInd />, action: () => { setIsReassigning(false); setActionDialog(false); setDriverPickerOpen(true); }, highlight: true },
                  ] : [
                    { label: 'Reassign to another Driver', icon: <AssignmentInd />, action: () => { setIsReassigning(true); setActionDialog(false); setDriverPickerOpen(true); }, highlight: true },
                  ]),
                  ...(selectedOrder.delivery_address ? [{
                    label: t('drivers.open_google_maps'), icon: <Navigation />,
                    action: () => { window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedOrder.delivery_address)}`, '_blank'); },
                  }] : []),
                  { label: t('drivers.print'), icon: <Print />, action: () => {} },
                  // Only show complete/cancel for non-completed orders
                  ...(!['delivered', 'completed', 'cancelled'].includes(selectedOrder.status) ? [
                    { label: t('drivers.complete_order'), icon: <CheckCircle />, action: () => completeOrder(selectedOrder) },
                    { label: t('drivers.cancel_order'), icon: <Cancel />, action: () => cancelOrder(selectedOrder), danger: true },
                  ] : []),
                ].map((item: any, i) => (
                  <ListItem key={i} onClick={item.action} sx={{
                    py: 1.3, px: 3, cursor: 'pointer',
                    backgroundColor: item.highlight ? 'rgba(255,152,0,0.08)' : 'transparent',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' }, borderBottom: '1px solid #2a2d3e',
                  }}>
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Box sx={{ color: item.danger ? '#F44336' : item.highlight ? '#FF9800' : '#9ca3af' }}>{item.icon}</Box>
                    </ListItemAvatar>
                    <ListItemText primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, color: item.danger ? '#F44336' : '#e0e0e0', fontSize: '0.88rem' }} />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* ─── Driver Picker Dialog (dark theme) ──────────────── */}
      <Dialog open={driverPickerOpen} onClose={() => { setDriverPickerOpen(false); setIsReassigning(false); setPickedDriverId(null); }} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#1e2139', border: '1px solid #3a3d52', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', maxHeight: '80vh' } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #2a2d3e' }}>
          <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '1.1rem' }}>
            {isReassigning ? 'Reassign Driver' : t('drivers.book_on_driver')}
          </Typography>
          <IconButton onClick={() => { setDriverPickerOpen(false); setIsReassigning(false); setPickedDriverId(null); }} size="small"
            sx={{ color: '#6b7280', '&:hover': { color: '#e0e0e0', backgroundColor: 'rgba(255,255,255,0.05)' } }}><Close sx={{ fontSize: 20 }} /></IconButton>
        </Box>

        {/* Driver list — single select */}
        <Box sx={{ px: 2, py: 1.5, overflowY: 'auto', maxHeight: 'calc(80vh - 140px)' }}>
          {[...drivers.filter(d => d.is_active)]
            .sort((a, b) => {
              const aOnline = a.status === 'available' || a.status === 'online' ? 0 : 1;
              const bOnline = b.status === 'available' || b.status === 'online' ? 0 : 1;
              return aOnline - bOnline;
            })
            .map((driver) => {
            if (isReassigning && selectedOrder?.rider_email === driver.email) return null;
            const isOnline = driver.status === 'available' || driver.status === 'online';
            const isSelected = pickedDriverId === driver.id;
            return (
              <Box key={driver.id}
                onClick={() => setPickedDriverId(isSelected ? null : driver.id)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.5, mb: 0.8, borderRadius: 2, cursor: 'pointer',
                  border: isSelected ? '2px solid #00C896' : isOnline ? '1px solid rgba(76,175,80,0.4)' : '1px solid #3a3d52',
                  backgroundColor: isSelected ? 'rgba(0, 200, 150,0.08)' : isOnline ? 'rgba(76,175,80,0.06)' : '#252838',
                  '&:hover': { backgroundColor: isSelected ? 'rgba(0, 200, 150,0.12)' : 'rgba(255,255,255,0.04)', borderColor: isSelected ? '#00C896' : '#5a5d72' },
                  transition: 'all 0.12s ease',
                }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: isSelected ? '#00C896' : isOnline ? 'rgba(76,175,80,0.15)' : '#2a2d42',
                }}>
                  {isSelected
                    ? <CheckCircle sx={{ fontSize: 20, color: '#1a1d2e' }} />
                    : <Person sx={{ fontSize: 20, color: isOnline ? '#4CAF50' : '#6b7280' }} />
                  }
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    {driver.map_code && (
                      <Box sx={{
                        backgroundColor: isOnline ? '#2196F3' : '#3a3d52',
                        color: '#fff', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.5px',
                        px: 0.8, py: 0.2, borderRadius: 1,
                      }}>{driver.map_code}</Box>
                    )}
                    <Typography sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: '0.95rem' }} noWrap>
                      {driver.name || driver.email}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: isOnline ? '#4CAF50' : '#6b7280', fontWeight: 500 }}>
                    {isOnline ? 'Online' : 'Offline'}{driver.deliveries_today > 0 ? ` · ${driver.deliveries_today} today` : ''}
                  </Typography>
                </Box>
                {isOnline && !isSelected && (
                  <FiberManualRecord sx={{ fontSize: 10, color: '#4CAF50' }} />
                )}
              </Box>
            );
          })}
          {drivers.filter(d => d.is_active).length === 0 && (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <DeliveryDining sx={{ fontSize: 44, color: '#3a3d52', mb: 1 }} />
              <Typography sx={{ color: '#6b7280', fontSize: '0.85rem' }}>{t('drivers.no_drivers_available')}</Typography>
            </Box>
          )}
        </Box>

        {/* Confirm button */}
        <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #2a2d3e' }}>
          <Button
            fullWidth variant="contained" disabled={!pickedDriverId}
            onClick={() => {
              const driver = drivers.find(d => d.id === pickedDriverId);
              if (!driver) return;
              isReassigning ? reassignDriver(driver) : assignDriver(driver);
              setPickedDriverId(null);
            }}
            sx={{
              py: 1.3, borderRadius: 2, fontWeight: 700, fontSize: '0.9rem', textTransform: 'none',
              backgroundColor: '#00C896', color: '#1a1d2e',
              '&:hover': { backgroundColor: '#7ecc10' },
              '&.Mui-disabled': { backgroundColor: '#2a2d42', color: '#4a4d62' },
            }}>
            {isReassigning ? 'Reassign' : 'Assign Driver'}
          </Button>
        </Box>
      </Dialog>

      {/* ─── Order Detail Dialog ─────────────────────────────── */}
      <Dialog open={orderDetailOpen} onClose={() => setOrderDetailOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#252838', border: '1px solid #3a3d52' } }}>
        {selectedOrder && (
          <>
            <DialogTitle sx={{ borderBottom: '1px solid #3a3d52', display: 'flex', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0' }}>{t('drivers.order_number', { number: String(selectedOrder.order_number) })}</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>{fmtTime(selectedOrder.created_at)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={t(statusKey(selectedOrder.status))} size="small"
                  sx={{ backgroundColor: `${statusColor(selectedOrder.status)}15`, color: statusColor(selectedOrder.status), fontWeight: 600 }} />
                <IconButton onClick={() => setOrderDetailOpen(false)} sx={{ color: '#6b7280' }}><Close /></IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {/* Customer source/registration badge */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                {(() => {
                  const isWebshop = selectedOrder.source === 'webshop' || selectedOrder.platform === 'webshop' || (selectedOrder.created_by || '').toLowerCase().includes('webshop');
                  const isQR = selectedOrder.source === 'qr_order' || selectedOrder.platform === 'qr_order';
                  const hasUid = !!selectedOrder.customer_uid;
                  const isGuest = selectedOrder.is_guest === true || (!hasUid && isWebshop);
                  return (
                    <>
                      {isWebshop && <Chip label="🌐 Webshop" size="small" sx={{ bgcolor: 'rgba(33,150,243,0.15)', color: '#64B5F6', fontSize: '0.65rem', fontWeight: 600, height: 22 }} />}
                      {isQR && <Chip label="📱 QR Code" size="small" sx={{ bgcolor: 'rgba(0, 200, 150,0.15)', color: '#00C896', fontSize: '0.65rem', fontWeight: 600, height: 22 }} />}
                      {hasUid && !isGuest && <Chip label="✅ Verified User" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#4CAF50', fontSize: '0.65rem', fontWeight: 600, height: 22 }} />}
                      {isGuest && <Chip label="👤 Guest Order" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#FF9800', fontSize: '0.65rem', fontWeight: 600, height: 22 }} />}
                      {!isWebshop && !isQR && <Chip label="🏪 POS / Phone" size="small" sx={{ bgcolor: 'rgba(158,158,158,0.15)', color: '#9E9E9E', fontSize: '0.65rem', fontWeight: 600, height: 22 }} />}
                    </>
                  );
                })()}
              </Box>

              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', mb: 1 }}>{t('drivers.customer')}</Typography>
              {[
                { icon: <Person sx={{ fontSize: 16 }} />, text: selectedOrder.customer_name || '—', label: 'Name' },
                selectedOrder.phone ? { icon: <Phone sx={{ fontSize: 16 }} />, text: selectedOrder.phone, label: 'Phone', clickable: `tel:${selectedOrder.phone}` } : null,
                selectedOrder.customer_email ? { icon: <Box component="span" sx={{ fontSize: 14 }}>✉</Box>, text: selectedOrder.customer_email, label: 'Email', clickable: `mailto:${selectedOrder.customer_email}` } : null,
                { icon: <LocationOn sx={{ fontSize: 16 }} />, text: selectedOrder.delivery_address || '—', label: 'Address' },
              ].filter(Boolean).map((row, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                  <Box sx={{ color: '#6b7280', minWidth: 20 }}>{row!.icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{(row as any).label}</Typography>
                    {(row as any).clickable ? (
                      <a href={(row as any).clickable} style={{ color: '#00C896', fontSize: '0.85rem', textDecoration: 'none' }}>{row!.text}</a>
                    ) : (
                      <Typography sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>{row!.text}</Typography>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Customer ID for support reference */}
              {selectedOrder.customer_uid && (
                <Typography sx={{ fontSize: '0.65rem', color: '#555', mt: 0.5, fontFamily: 'monospace' }}>
                  ID: {selectedOrder.customer_uid.substring(0, 16)}...
                </Typography>
              )}

              {/* FRAUD PREVENTION #1: Delivery PIN — visible to staff for support
                  Driver must ask customer for this PIN before marking delivered */}
              {selectedOrder.delivery_pin && !selectedOrder.delivery_pin_verified && selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                <Box sx={{
                  mt: 1.5, p: 1.2, borderRadius: 1.5,
                  background: 'linear-gradient(135deg, rgba(0, 200, 150,0.1), rgba(76,175,80,0.05))',
                  border: '1px solid rgba(0, 200, 150,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#00C896', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      🔐 Delivery PIN
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#9ca3af', mt: 0.2 }}>
                      Customer must give this to driver
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontSize: '1.6rem', fontWeight: 800, color: '#00C896',
                    fontFamily: 'monospace', letterSpacing: '0.3rem',
                  }}>
                    {selectedOrder.delivery_pin}
                  </Typography>
                </Box>
              )}
              {selectedOrder.delivery_pin && selectedOrder.delivery_pin_verified && (
                <Box sx={{ mt: 1.5, p: 1, borderRadius: 1.5, bgcolor: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)' }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#4CAF50', fontWeight: 600 }}>
                    ✅ Delivery PIN verified
                  </Typography>
                </Box>
              )}
              {selectedOrder.rider_name && (
                <>
                  <Divider sx={{ my: 1.5, borderColor: '#2a2d3e' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', mb: 0.5 }}>{t('drivers.assigned_driver')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#2196F3' }}>{selectedOrder.rider_name.charAt(0)}</Avatar>
                    <Typography sx={{ color: '#e0e0e0', fontSize: '0.85rem', fontWeight: 600 }}>{selectedOrder.rider_name}</Typography>
                  </Box>
                </>
              )}
              <Divider sx={{ my: 1.5, borderColor: '#2a2d3e' }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', mb: 1 }}>{t('drivers.items_count', { count: String(selectedOrder.item_count) })}</Typography>
              {selectedOrder.items?.map((item: any, i: number) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #2a2d3e' }}>
                  <Typography sx={{ color: '#e0e0e0', fontSize: '0.85rem' }}>{item.quantity || 1}× {item.name || item.menu_item_name}</Typography>
                  <Typography sx={{ color: '#00C896', fontSize: '0.85rem', fontWeight: 600 }}>€{(item.total || item.price || 0).toFixed(2)}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1, borderTop: '2px solid #3a3d52' }}>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0' }}>{t('drivers.total')}</Typography>
                <Typography sx={{ fontWeight: 700, color: '#00C896', fontSize: '1.1rem' }}>€{selectedOrder.total.toFixed(2)}</Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #3a3d52', gap: 1 }}>
              <Button onClick={() => setOrderDetailOpen(false)} variant="outlined" sx={{ borderColor: '#3a3d52', color: '#9ca3af', textTransform: 'none' }}>{t('drivers.close')}</Button>
              {selectedOrder.delivery_address && (
                <Button variant="outlined" startIcon={<Navigation />}
                  onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedOrder.delivery_address)}`, '_blank')}
                  sx={{ borderColor: '#2196F3', color: '#2196F3', textTransform: 'none' }}>
                  {t('drivers.google_maps')}
                </Button>
              )}
              {!selectedOrder.rider_email && (
                <Button variant="contained" startIcon={<AssignmentInd />} onClick={() => { setOrderDetailOpen(false); setDriverPickerOpen(true); }}
                  sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#FF9800', '&:hover': { backgroundColor: '#e68900' } }}>{t('drivers.book_on_driver')}</Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DriverScreen;
